import { useMutation } from 'react-query';
import { toggleAffirmPaymentMethod } from '@frontend/api-payments';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { getMixedValue, useSelectedLocations, useMerchants } from '@frontend/payments-hooks';
import { ContentLoader, SwitchField, useFormField, useAlert } from '@frontend/design-system';
import { usePaymentMethods } from '../../../hooks/settings/use-payment-methods';
import { useCanDoActionsMulti } from '../../../hooks/use-can-do-actions-multi';
import * as styles from '../styles/payment-settings-page.styles';

export const AffirmToggleSwitch = () => {
  const alerts = useAlert();
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });
  const { locationIds } = useSelectedLocations();
  const { isPaymentsBillingManager } = useCanDoActionsMulti(locationIds);
  const { stripeAffirmPaymentLocations } = usePaymentMethods();

  const { refetch, merchantsData } = useMerchants(locationIds);
  const canToggleAffirmPayment = stripeAffirmPaymentLocations.every(
    (locationId) => !!merchantsData[locationId]?.stripeId
  );
  const affirmAvailableValues = stripeAffirmPaymentLocations.map(
    (locationId) => merchantsData[locationId]?.merchant?.affirmAvailable
  );
  const switchValue = getMixedValue(affirmAvailableValues);

  const affirmSwitchProps = useFormField(
    {
      value: switchValue === true,
      type: 'switch',
    },
    [switchValue]
  );

  const { mutate, isLoading } = useMutation({
    mutationFn: () => {
      const mutationPromises = stripeAffirmPaymentLocations.map((locationId) => {
        const stripeId = merchantsData[locationId]?.stripeId;
        if (stripeId) {
          return toggleAffirmPaymentMethod(stripeId, !affirmSwitchProps.value, locationId);
        }
        return null;
      });

      return Promise.all(mutationPromises);
    },
    onError: (error) => {
      if (
        error instanceof HttpError &&
        typeof error.data === 'object' &&
        'error' in error.data &&
        error.status === 400
      ) {
        alerts.error(error.data.error);
      } else alerts.error(t('Error toggling affirm payment option'));
    },
    onSettled: () => {
      refetch();
    },
  });

  return (
    <>
      <ContentLoader show={isLoading} message={t('Toggling Affirm payments')} />
      <SwitchField
        {...affirmSwitchProps}
        name='enable-stripe-affirm-payment'
        label={''}
        labelPlacement='left'
        disabled={!canToggleAffirmPayment || isLoading || !isPaymentsBillingManager}
        onChange={() => mutate()}
        css={styles.ffSwitch()}
        indeterminate={switchValue === 'mixed'}
      ></SwitchField>
    </>
  );
};
